import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, CheckCircle, ChevronDown } from 'lucide-react';

const services = ['Software Development', 'Web & Email Services', 'Microsoft Cloud', 'IT Consulting', 'Business Systems', 'General Inquiry'];

const publicUrl = process.env.PUBLIC_URL || '';

export default function Contact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', message: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setSubmitted(true);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.875rem 1rem', borderRadius: 10,
    background: 'rgba(255,107,0,0.03)', border: '1px solid var(--gray-light)',
    color: 'var(--text)', fontSize: '0.9rem', outline: 'none', fontFamily: 'DM Sans',
    transition: 'border-color 0.2s',
  };

  return (
    <section id="contact" style={{ padding: '8rem 2rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <span style={{
            display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--text)', marginBottom: '1rem',
            padding: '0.3rem 0.9rem', borderRadius: 100,
            background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
          }}>Get In Touch</span>
          <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)', letterSpacing: '-0.02em', color: 'var(--text)' }}>
            Let's Build Something <span style={{ color: 'var(--orange)' }}>Together</span>
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '3rem' }}>
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2.5rem' }}>
              {[
                { icon: MapPin, label: 'Visit Us', value: 'Stand Number 100/647 Off Joel Kapililisha Street, Ibex Hill, Lusaka, Zambia', color: 'var(--orange)' },
                { icon: Phone, label: 'Call Us', value: '+260 772 420 450', color: 'var(--orange)' },
                { icon: Mail, label: 'Email Us', value: 'info@findurainfosys.com', color: 'var(--orange)' },
              ].map(({ icon: Icon, label, value, color }) => (
                <div key={label} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: `${color}15`, border: `1px solid ${color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={16} color={color} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.2rem' }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text)' }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div style={{
              width: '100%', borderRadius: 16, overflow: 'hidden',
              border: '1px solid var(--gray-light)',
            }}>
              <div style={{ position: 'relative', width: '100%', height: 200 }}>
                <iframe
                  title="Findura Infosys location"
                  src="https://www.google.com/maps?q=Stand+Number+100%2F647+Joel+Kapililisha+Road%2C+Ibex+Hill%2C+Lusaka%2C+Zambia&z=16&output=embed"
                  width="100%"
                  height={200}
                  style={{ border: 0, display: 'block' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                {/* Branded logo pin overlay */}
                <div style={{
                  position: 'absolute', left: '50%', top: '50%',
                  transform: 'translate(-50%, -100%)', pointerEvents: 'none',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%',
                    background: 'var(--white)', border: '3px solid var(--orange)',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    <img src={`${publicUrl}/logo192.png`} alt="Findura Infosys" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                  </div>
                  <div style={{
                    width: 0, height: 0, marginTop: -2,
                    borderLeft: '7px solid transparent', borderRight: '7px solid transparent',
                    borderTop: '10px solid var(--orange)',
                    filter: 'drop-shadow(0 3px 2px rgba(0,0,0,0.25))',
                  }} />
                </div>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Stand+Number+100%2F647+Joel+Kapililisha+Road%2C+Ibex+Hill%2C+Lusaka%2C+Zambia"
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                  padding: '0.6rem', fontSize: '0.75rem', color: 'var(--orange)',
                  background: 'rgba(255,107,0,0.05)', borderTop: '1px solid var(--gray-light)',
                }}
              ><MapPin size={13} /> Open in Google Maps →</a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              padding: '2.5rem',
              background: 'rgba(255,255,255,0.5)',
              border: '1px solid var(--gray-light)',
              borderRadius: 20,
            }}
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ textAlign: 'center', padding: '3rem 0' }}
              >
                <CheckCircle size={60} color="var(--orange)" style={{ margin: '0 auto 1.5rem' }} />
                <h3 style={{ fontFamily: 'Syne', fontSize: '1.5rem', marginBottom: '0.75rem', color: 'var(--text)' }}>Message Sent!</h3>
                <p style={{ color: 'var(--text-soft)', fontSize: '0.9rem' }}>We'll get back to you within 24 hours.</p>
              </motion.div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Full Name *</label>
                    <input style={inputStyle} placeholder="Full Name" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Company Name</label>
                    <input style={inputStyle} placeholder="Your Company" value={form.company}
                      onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Email Address *</label>
                    <input style={inputStyle} type="email" placeholder="example@company.com" value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Phone Number</label>
                    <input style={inputStyle} placeholder="+260 7XX XXX XXX" value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }} />
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Service of Interest</label>
                  <div style={{ position: 'relative' }}>
                    <ChevronDown
                      size={16}
                      color="var(--orange)"
                      style={{
                        position: 'absolute', left: 12, top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    />
                    <select
                      style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', paddingLeft: '2rem' }}
                      value={form.service}
                      onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
                      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }}
                    >
                      <option value="" style={{ background: '#FFFFFF' }}>Select a service...</option>
                      {services.map(s => <option key={s} value={s} style={{ background: '#FFFFFF', color: 'var(--text)' }}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.4rem' }}>Message</label>
                  <textarea
                    style={{ ...inputStyle, minHeight: 100, resize: 'vertical' as const }}
                    placeholder="Tell us about your project..."
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    onFocus={e => { (e.target as HTMLElement).style.borderColor = 'var(--orange)'; }}
                    onBlur={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-light)'; }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <input
                    type="checkbox" id="consent"
                    checked={form.consent}
                    onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                    style={{ marginTop: '0.2rem', accentColor: 'var(--orange)', cursor: 'pointer' }}
                  />
                  <label htmlFor="consent" style={{ fontSize: '0.8rem', color: 'var(--text-soft)', lineHeight: 1.5, cursor: 'pointer' }}>
                    I consent to Findura Infosys storing my data in accordance with Zambian data protection practices.
                  </label>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !form.name || !form.email || !form.consent}
                  style={{
                    width: '100%', padding: '1rem', borderRadius: 12,
                    background: 'var(--orange)', color: 'var(--white)',
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                    boxShadow: '0 0 20px var(--electric-glow)',
                    opacity: (!form.name || !form.email || !form.consent) ? 0.5 : 1,
                    transition: 'all 0.25s', cursor: (!form.name || !form.email || !form.consent) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Sending...' : <><Send size={16} /> Send Message</>}
                </button>
              </>
            )}
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          #contact > div > div:last-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
